import { useMutation, useQuery } from '@tanstack/react-query';

/**
 * api.ts — la entrada al portal. Módulo 18, capa A.
 *
 * Como el formulario público, **nada de esto usa `lib/api`**: aquí todavía no
 * hay sesión, y `lib/api` delega en el `API` global, donde viven el token y el
 * 401 que devuelve al login. Un 401 en la pantalla de login que te devuelve a la
 * pantalla de login es un bucle, no un mensaje.
 *
 * `Auth.login` sí es el global, y eso es deliberado: es quien guarda el token y
 * el usuario. Reimplementarlo aquí daría dos versiones de la sesión.
 */

export interface Marca {
  portal_name: string;
  company_name: string;
  login_subtitle: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  show_landing: boolean;
}

/**
 * La marca del portal: nombre, logo, lema.
 *
 * Es **pública y sin sesión** —la leen la portada y el login— y el servidor la
 * cachea 60 s. Antes la pintaba `applyBranding()` metiéndola en el DOM con
 * selectores posicionales (`.login-hero-brand div div:first-child`) después de
 * que la pantalla ya existiera; aquí es un dato que el componente lee.
 */
export function useMarca() {
  return useQuery({
    queryKey: ['acceso', 'marca'] as const,
    queryFn: async (): Promise<Marca> => {
      const res = await fetch('/api/auth/branding');
      if (!res.ok) throw new Error('No se pudo cargar la personalización');
      return res.json();
    },
    // La marca no cambia entre un intento de entrada y el siguiente.
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Entrar. Delega en `Auth.login`, que es quien guarda el token. */
export function useEntrar() {
  return useMutation({
    mutationFn: (v: { correo: string; clave: string }) =>
      Auth.login(v.correo, v.clave) as Promise<unknown>,
  });
}

async function publico<T>(ruta: string, cuerpo: unknown): Promise<T> {
  const res = await fetch(ruta, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  let datos: unknown = null;
  try {
    datos = await res.json();
  } catch {
    // Un cuerpo que no es JSON no añade nada a un error que ya está en curso.
  }
  if (!res.ok) {
    const detalle = (datos as { detail?: string } | null)?.detail;
    const err: Error & { status?: number } = new Error(detalle || 'No se pudo completar');
    err.status = res.status;
    throw err;
  }
  return datos as T;
}

export function usePedirEnlace() {
  return useMutation({
    mutationFn: (correo: string) =>
      publico<{ message?: string }>('/api/auth/forgot-password', { email: correo }),
  });
}

export function useRestablecerClave() {
  return useMutation({
    mutationFn: (v: { token: string; clave: string }) =>
      publico<{ message?: string }>('/api/auth/reset-password', {
        token: v.token, new_password: v.clave,
      }),
  });
}

/** El token del enlace de recuperación: `#reset?token=…`. */
export function tokenDeRestablecer(): string {
  const h = window.location.hash || '';
  if (!h.startsWith('#reset')) return '';
  const qs = h.includes('?') ? h.split('?')[1] : '';
  return new URLSearchParams(qs ?? '').get('token') ?? '';
}
